export type Operator = "+" | "-" | "×" | "÷";

export type CalculatorState = {
  display: string;
  stored: number | null;
  operator: Operator | null;
  overwrite: boolean;
};

export function initialCalculatorState(): CalculatorState {
  return {
    display: "0",
    stored: null,
    operator: null,
    overwrite: true,
  };
}

export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return "Error";
  if (Math.abs(value) > 1e15) return value.toExponential(6);

  const rounded = Math.round(value * 1e10) / 1e10;
  return String(rounded);
}

function parseDisplay(display: string): number {
  return Number.parseFloat(display);
}

function compute(a: number, b: number, operator: Operator): number {
  switch (operator) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "×":
      return a * b;
    case "÷":
      return b === 0 ? Number.NaN : a / b;
  }
}

export function inputDigit(
  state: CalculatorState,
  digit: string,
): CalculatorState {
  if (state.display === "Error") return initialCalculatorState();

  let display = state.overwrite ? digit : state.display + digit;
  if (!state.overwrite && state.display === "0") display = digit;
  if (state.overwrite && digit === "0") display = "0";

  const digits = display.replace("-", "").replace(".", "");
  if (digits.length > 15) return state;

  return { ...state, display, overwrite: false };
}

export function inputDecimal(state: CalculatorState): CalculatorState {
  if (state.display === "Error") return initialCalculatorState();
  if (state.overwrite) {
    return { ...state, display: "0.", overwrite: false };
  }
  if (state.display.includes(".")) return state;
  return { ...state, display: `${state.display}.`, overwrite: false };
}

export function toggleSign(state: CalculatorState): CalculatorState {
  if (state.display === "Error" || state.display === "0") return state;
  return {
    ...state,
    display: state.display.startsWith("-")
      ? state.display.slice(1)
      : `-${state.display}`,
  };
}

export function applyPercent(state: CalculatorState): CalculatorState {
  const value = parseDisplay(state.display);
  if (!Number.isFinite(value)) return state;
  return {
    ...state,
    display: formatNumber(value / 100),
    overwrite: true,
  };
}

export function clearAll(): CalculatorState {
  return initialCalculatorState();
}

export function backspace(state: CalculatorState): CalculatorState {
  if (state.overwrite || state.display === "Error") return state;

  if (state.display.length === 1 || state.display === "-") {
    return { ...state, display: "0", overwrite: true };
  }

  const next = state.display.slice(0, -1);
  return {
    ...state,
    display: next === "-" ? "0" : next,
    overwrite: next === "0",
  };
}

export function setOperator(
  state: CalculatorState,
  operator: Operator,
): CalculatorState {
  const current = parseDisplay(state.display);
  if (!Number.isFinite(current)) return state;

  if (state.stored !== null && state.operator && !state.overwrite) {
    const result = compute(state.stored, current, state.operator);
    return {
      display: formatNumber(result),
      stored: result,
      operator,
      overwrite: true,
    };
  }

  return {
    ...state,
    stored: current,
    operator,
    overwrite: true,
  };
}

export function calculate(state: CalculatorState): CalculatorState {
  if (state.operator === null || state.stored === null) return state;

  const current = parseDisplay(state.display);
  const result = compute(state.stored, current, state.operator);

  return {
    display: formatNumber(result),
    stored: null,
    operator: null,
    overwrite: true,
  };
}

export type HistoryEntry = {
  id: string;
  expression: string;
  result: string;
};

export function buildHistoryEntry(state: CalculatorState): HistoryEntry | null {
  if (state.operator === null || state.stored === null) return null;

  const current = parseDisplay(state.display);
  const result = compute(state.stored, current, state.operator);

  return {
    id: crypto.randomUUID(),
    expression: `${formatNumber(state.stored)} ${state.operator} ${formatNumber(current)}`,
    result: formatNumber(result),
  };
}

export function stateFromResult(result: string): CalculatorState {
  return {
    display: result,
    stored: null,
    operator: null,
    overwrite: true,
  };
}

export function expressionHint(state: CalculatorState): string | null {
  if (state.operator === null || state.stored === null) return null;
  return `${formatNumber(state.stored)} ${state.operator}`;
}
